import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { appSettingReducer } from './features/appSettings';
import { appSettingsMiddleware } from './features/appSettings/store/middleware';
import {
  fetchUserInfoMiddleware,
  loginMiddleware,
  logoutMiddleware,
  resetPasswordRequestMiddleware,
} from './features/auth/middleware';
import { authSlicerReducer } from './features/auth/store/authSlice';
import { EStoreLoadAgencyMiddleware, EStoreReducer, EStoreSubmitDataMiddleware } from './features/client/EStore/store';
import { HomeToStoreLoadAgencyMiddleware, HomeToStoreReducer } from './features/client/HomToStoreContainer/store';
import { HomeToStoreSubmitDataMiddleware } from './features/client/HomToStoreContainer/store/middlewares/HomeToStoreSubmitDataMiddleware.middleware';
import { InStoreReducer } from './features/client/InStore';
import {
  InStoreLoadAgencyMiddleware,
  InStoreSubmitDataMiddleware,
  internetCampaignReducer,
} from './features/client/InStore/store';
import { languageReducer } from './features/language/store';
import { ocmMiddleware } from './features/ocm/store';
import { ocmReducer } from './features/ocm/store/ocmSlicer';
import { posReducer } from './features/pos/store';
import { printerReducer } from './features/printer/store';
import { useSlicerReducer } from './features/user/store';

const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['auth', 'user', 'appConfigs', 'pos', 'printer', 'eStore', 'inStore', 'homeToStore', 'internetCampaign'],
};

const authPersistConfig = {
  key: 'auth',
  storage,
  blacklist: ['error', 'isLoading', 'resetPasswordSentSuccess'],
};

const userPersistConfig = {
  key: 'user',
  storage,
  blacklist: ['error', 'isLoading'],
};

const appConfigsPersistConfig = {
  key: 'appConfigs',
  storage,
  blacklist: ['error', 'isLoading'],
};

const posPersistConfig = {
  key: 'pos',
  storage,
  blacklist: ['error', 'isLoading'],
};

const printerPersistConfig = {
  key: 'printer',
  storage,
  blacklist: ['error', 'isLoading'],
};

const ocmPersistConfig = {
  key: 'ocm',
  storage,
  blacklist: ['error', 'isLoading', 'outOfSold', 'firstLoad', 'data'],
};

const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authSlicerReducer),
  user: persistReducer(userPersistConfig, useSlicerReducer),
  appConfigs: persistReducer(appConfigsPersistConfig, appSettingReducer),
  pos: persistReducer(posPersistConfig, posReducer),
  printer: persistReducer(printerPersistConfig, printerReducer),
  eStore: EStoreReducer,
  inStore: InStoreReducer,
  homeToStore: HomeToStoreReducer,
  ocm: persistReducer(ocmPersistConfig, ocmReducer),
  language: languageReducer,
  internetCampaign: internetCampaignReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const middleWares = [
  loginMiddleware,
  resetPasswordRequestMiddleware,
  fetchUserInfoMiddleware,
  logoutMiddleware,
  appSettingsMiddleware,
  InStoreLoadAgencyMiddleware,
  EStoreLoadAgencyMiddleware,
  EStoreSubmitDataMiddleware,
  InStoreSubmitDataMiddleware,
  ocmMiddleware,
  HomeToStoreLoadAgencyMiddleware,
  HomeToStoreSubmitDataMiddleware,
];

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV === 'development',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(middleWares),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
